/* eslint-disable no-nested-ternary */
import { Link, matchPath, useLocation } from "react-router-dom";

import { AppWhiteLogoIcon } from "components/svg-icons";
import AccountMenu from "components/account-menu";
import {
  ProjectContext,
  TProjectContext,
  isPermissionNotGrantted
} from "context/ProjectProvider";
import { useContext, useEffect, useMemo, useState } from "react";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { matchUserProjectRole } from "utils/utils";
import { Tooltip } from "antd";
import { DATE_FORMAT_MMDDYYYY_HHMM_A_Z, DateUtils } from "utils/dateutils";
// import { useShowDesignAndSpec } from "hooks/common";
import ProjectLogoIcon from "components/svg-icons/project-logo";
import { EUserTypes, ProjectPermissionEnum } from "../../constants";
import styles from "./navbar.module.scss";

type MenuLink = {
  label: any;
  key: string;
};

export function DateTimePanel() {
  const [time, setTime] = useState(DateUtils.dateTimeObj());
  useEffect(() => {
    const timer = setInterval(() => {
      // Creates an interval which will update the current data every minute
      // This will trigger a rerender every component that uses the useDate hook.
      setTime(DateUtils.dateTimeObj());
    }, 1000);
    return () => {
      clearInterval(timer); // Return a funtion to clear the timer so that it will stop being called on unmount
    };
  }, []);
  return (
    <Tooltip
      className="w-28"
      title={time.format(DATE_FORMAT_MMDDYYYY_HHMM_A_Z)}
    >
      {time.format("hh:mmA (z)")}
    </Tooltip>
  );
}

type NavbarProps = {
  menuLinks?: MenuLink[];
};

function Navbar(props: NavbarProps) {
  const { menuLinks = [] } = props;
  const { pathname } = useLocation();

  const {
    subscriptionRole,
    isFeatureFlagEnabled: isFeatureFlagSubscriptionEnabled
  } = useContext(SubscriptionContext);
  const {
    tokenContents,
    projectDetails,
    isFeatureFlagEnabled: isFeatureFlagProjectEnabled
  }: TProjectContext = useContext(ProjectContext);

  const isFeatureFlagEnabled =
    isFeatureFlagProjectEnabled || isFeatureFlagSubscriptionEnabled;

  const isBuyoutFeatureEnabled = isFeatureFlagEnabled("BUYOUT");

  const filteredMenuLinks = useMemo(() => {
    const cannotViewProjectReports = isPermissionNotGrantted(
      ProjectPermissionEnum.ViewProjectReports,
      tokenContents?.role!
    );
    const cannotViewDesignPage = isPermissionNotGrantted(
      ProjectPermissionEnum.ViewDesignPage,
      tokenContents?.role!
    );
    let fMenuLinks = menuLinks;
    if (projectDetails && cannotViewProjectReports) {
      fMenuLinks = fMenuLinks.filter((item) => {
        return item.label !== "Reports";
      });
    }

    if (!isBuyoutFeatureEnabled || cannotViewDesignPage) {
      fMenuLinks = fMenuLinks.filter((item) => {
        return !(
          item.key.includes("/design-packages") ||
          item.key.includes("/bid-packages")
        );
      });
    }

    if (subscriptionRole < RoleSubscriptionEnum.subscription_admin) {
      fMenuLinks = fMenuLinks.filter((item) => {
        return item.key !== "/analytics";
      });
    }

    if (subscriptionRole < RoleSubscriptionEnum.subscription_admin) {
      fMenuLinks = fMenuLinks.filter((item) => {
        return item.key !== "/settings";
      });
    }
    return fMenuLinks;
  }, [
    isBuyoutFeatureEnabled,
    menuLinks,
    projectDetails,
    subscriptionRole,
    tokenContents?.role
  ]);

  const isCurrentUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isCurrentUserSC = matchUserProjectRole(
    EUserTypes.SUBCONTRACTOR,
    tokenContents?.role
  );

  const isCurrentUserArchitect = matchUserProjectRole(
    EUserTypes.ARCHITECT,
    tokenContents?.role
  );

  return (
    <div className="flex justify-between w-full bg-ciq-primary-200 text-white hover:text-white px-4">
      <div className="flex">
        <div className="flex items-center border-0 border-r border-solid py-2 pr-3 border-black">
          <Link
            to="/projects"
            className="flex"
            data-testid="navbar-link-app-logo"
          >
            <div className="w-[120px] flex">
              <AppWhiteLogoIcon />
            </div>
          </Link>
        </div>

        {projectDetails?.id &&
          (isCurrentUserGC || isCurrentUserSC || isCurrentUserArchitect ? (
            <Link
              to={`/project/${projectDetails.id}/dashboard`}
              className="flex items-center space-x-2 text-white hover:text-white pl-3"
              data-testid="navbar-link-project-logo"
            >
              <ProjectLogoIcon size={30} />
              <div className="flex space-x-1">
                <div className="font-medium truncate max-w-xs">
                  <TruncatedTextWithTooltip text={projectDetails?.name} />
                </div>
                <div>|</div>
                <DateTimePanel />
              </div>
            </Link>
          ) : (
            projectDetails?.name
          ))}
      </div>
      <div className="flex space-x-4 items-center h-full">
        <div className="flex h-full">
          {filteredMenuLinks.map((x) => (
            <Link
              key={x.key}
              to={x.key}
              className={
                matchPath(pathname, { path: x.key })
                  ? `hover:drop-shadow-[0_0_0.3px_rgba(0,0,0,1)] ${styles.menuActiveIconCSS}`
                  : `hover:drop-shadow-[0_0_0.3px_rgba(0,0,0,1)] ${styles.menuIconCSS}`
              }
            >
              {x.label}
            </Link>
          ))}
        </div>
        <div className="pl-3 py-2 flex justify-center items-center border-0 border-l border-solid border-black">
          <AccountMenu />
        </div>
      </div>
    </div>
  );
}

export default Navbar;
